import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { db } from '../../../lib/firebase';
import Modal from '../../common/Modal';
import { BanknotesIcon } from '@heroicons/react/24/outline';

export interface OrgRates {
  /** belső önköltség / óra (Ft), ha a munkatársnál nincs megadva */
  defaultCostRate: number;
  /** kiszámlázási óradíj (Ft, nettó) – a számlázható órák értékéhez */
  billableRate: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  users: { id: string; displayName?: string; email?: string; hourlyRate?: number }[];
  onSaved?: () => void;
}

/** Óradíjak: cég alapértelmezett önköltség + kiszámlázási díj, és munkatársankénti önköltség. */
const HourlyRatesModal: React.FC<Props> = ({ isOpen, onClose, organizationId, users, onSaved }) => {
  const [rates, setRates] = useState<OrgRates>({ defaultCostRate: 0, billableRate: 0 });
  const [userRates, setUserRates] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoading(true);
      try {
        const snap = await getDoc(doc(db, 'organizations', organizationId));
        const r = (snap.data() as any)?.rates || {};
        setRates({ defaultCostRate: Number(r.defaultCostRate || 0), billableRate: Number(r.billableRate || 0) });
        const ur: Record<string, string> = {};
        users.forEach(u => { ur[u.id] = u.hourlyRate != null ? String(u.hourlyRate) : ''; });
        setUserRates(ur);
      } catch (e) { console.error(e); } finally { setLoading(false); }
    })();
  }, [isOpen, organizationId, users]);

  const save = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'organizations', organizationId), { rates: { defaultCostRate: Number(rates.defaultCostRate) || 0, billableRate: Number(rates.billableRate) || 0 }, updatedAt: serverTimestamp() }, { merge: true });
      for (const u of users) {
        const v = userRates[u.id];
        const num = v === '' || v == null ? null : Number(v);
        if ((u.hourlyRate ?? null) !== num) {
          await updateDoc(doc(db, 'users', u.id), { hourlyRate: num });
        }
      }
      toast.success('Óradíjak mentve');
      onSaved?.();
      onClose();
    } catch (e: any) { console.error(e); toast.error(e?.message || 'A mentés nem sikerült'); } finally { setSaving(false); }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Óradíjak" size="2xl">
      <div className="space-y-4">
        <p className="text-xs text-gray-500 dark:text-gray-400">A <b>önköltség</b> az, amibe egy munkaóra a cégnek kerül (bér + járulék + rezsi / óra) – ebből számolja a riport a ráfordítást. A <b>kiszámlázási óradíj</b> az, amit az ügyfélnek számlázol – ebből a számlázható órák értékét. Mindkettő nettó Ft.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="crm-card p-3 block">
            <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Alapértelmezett önköltség / óra</span>
            <div className="flex items-center gap-2 mt-1"><input type="number" min={0} step={100} value={rates.defaultCostRate} onChange={e => setRates({ ...rates, defaultCostRate: Number(e.target.value) })} className="crm-input-compact w-full border rounded-md px-2 tabular-nums" /><span className="text-sm text-gray-500">Ft</span></div>
          </label>
          <label className="crm-card p-3 block">
            <span className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-semibold">Kiszámlázási óradíj</span>
            <div className="flex items-center gap-2 mt-1"><input type="number" min={0} step={100} value={rates.billableRate} onChange={e => setRates({ ...rates, billableRate: Number(e.target.value) })} className="crm-input-compact w-full border rounded-md px-2 tabular-nums" /><span className="text-sm text-gray-500">Ft</span></div>
          </label>
        </div>
        <div className="crm-card overflow-hidden">
          <div className="px-3 py-2 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] text-xs font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-1.5"><BanknotesIcon className="w-4 h-4 text-gray-400" />Munkatársankénti önköltség (üres = alapértelmezett)</div>
          {loading ? <div className="p-4 text-sm text-gray-500">Betöltés…</div> : (
            <div className="divide-y divide-[var(--crm-border)] max-h-[50vh] overflow-y-auto">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-3 px-3 py-1.5">
                  <div className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-100 truncate">{u.displayName || u.email}<span className="text-xs text-gray-400 ml-1">{u.displayName ? u.email : ''}</span></div>
                  <input type="number" min={0} step={100} value={userRates[u.id] ?? ''} onChange={e => setUserRates({ ...userRates, [u.id]: e.target.value })} placeholder={String(rates.defaultCostRate || '')} className="crm-input-compact w-28 border rounded-md px-2 text-right tabular-nums" />
                  <span className="text-xs text-gray-500 w-8">Ft/ó</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="crm-btn crm-btn-ghost" disabled={saving}>Mégse</button>
          <button type="button" onClick={save} className="crm-btn crm-btn-primary" disabled={saving}>{saving ? 'Mentés…' : 'Mentés'}</button>
        </div>
      </div>
    </Modal>
  );
};

export default HourlyRatesModal;
